var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { DataContext } from '../player/DataContext';
import globalStore from '../store';
import { saveState } from '../helper/stateManagerHelper';
import { registerMultiSelectSaveEntries } from '../helper/multiSelectSaveCoordinator';
import customEventHandler from '../helper/customEvents';
import { bringManipulativesToFront } from '../helper/helper';
import { computeFractionTileNumberLineDetachUpdates } from '../helper/snapHelper';
import { computeTickPositions, computeLabelPositions, computeLineWidth, computePxPerUnit, gcd, } from '../helper/numberLineHelper';
import { ARROWHEAD_SIZE, LABEL_OFFSET, LINE_Y, TOTAL_SVG_HEIGHT, GRID_SIZE, DEFAULT_JUMP_BAR_LENGTH, MAX_UNITS, } from '../constants/numberLineConfig';
import NumberLineLabel from './NumberLineLabel';
import { v4 as uuidv4 } from 'uuid';
import '../styles/widgets/numberLineManipulative.scss';
import '../styles/widgets/numberLineScaleSnapNotice.scss';
import { t } from 'i18next';
import Popup from '../components/Popup';
import ButtonComponent from '../components/ButtonComponent';
import { useDuplicateAnnounce, useCreationAnnounce, } from '../helper/hooks/useDuplicateAnnounce';
import useAnnouncer from '../hooks/useAnnouncer';
var NumberLineManipulative = function (_a) {
    var _b, _c;
    var data = _a.data, calledFrom = _a.calledFrom;
    var isPreview = calledFrom === 'popup';
    var _d = useContext(DataContext), setManipulative = _d.setManipulative, selectedTool = _d.selectedTool, setSelectedTool = _d.setSelectedTool, panningEnable = _d.panningEnable, setManiplulatorComponentVisible = _d.setManiplulatorComponentVisible, setActiveManipulator = _d.setActiveManipulator, setSelectedManipulativeId = _d.setSelectedManipulativeId, manipulative = _d.manipulative, setContextMenu = _d.setContextMenu;
    var zoomLevel = globalStore.useStoreState(function (store) { return store.playerStore; }).zoomLevel;
    var playerBgPattern = globalStore.useStoreState(function (store) { return store.playerStore; }).playerBgPattern;
    var id = data.id, center = data.center, dimensions = data.dimensions, _e = data.scale, scale = _e === void 0 ? 1 : _e, lineMode = data.lineMode, startNum = data.startNum, endNum = data.endNum, intervalNum = data.intervalNum, labelIntervalNum = data.labelIntervalNum, gridSquaresPerUnit = data.gridSquaresPerUnit, labelsVisible = data.labelsVisible, _f = data.unmaskedLabels, unmaskedLabels = _f === void 0 ? [] : _f, intervalStr = data.interval, labelIntervalStr = data.labelInterval, lock = data.lock, jumpBarSnapToGrid = data.jumpBarSnapToGrid, scalingToManipulatives = data.scalingToManipulatives;
    var isFocused = selectedTool.includes(id);
    var positionLock = (_b = lock === null || lock === void 0 ? void 0 : lock.positioning) !== null && _b !== void 0 ? _b : false;
    // "Snap jumps to underlying grid" only has an effect at scale 1 (see
    // NumberLineJumpBar.tsx's `isScaleOne` gate). Warn the teacher the first
    // time the line leaves scale 1 while the toggle is on, since the drag
    // behavior silently reverts to plain tick-snapping otherwise. Shown at
    // most once per number line (per mount) — a `hasShownRef` guard, not just
    // the scale-1→non-1 edge, so scaling back to 1 and away again doesn't
    // re-nag with the same notice.
    var _g = useState(false), showScaleSnapNotice = _g[0], setShowScaleSnapNotice = _g[1];
    var prevScaleRef = useRef(scale);
    var hasShownScaleSnapNoticeRef = useRef(false);
    useEffect(function () {
        var prevScale = prevScaleRef.current;
        var wasScaleOne = Math.abs(prevScale - 1) < 1e-6;
        var isScaleOne = Math.abs(scale - 1) < 1e-6;
        if (!isPreview &&
            jumpBarSnapToGrid &&
            wasScaleOne &&
            !isScaleOne &&
            !hasShownScaleSnapNoticeRef.current) {
            hasShownScaleSnapNoticeRef.current = true;
            setShowScaleSnapNotice(true);
        }
        prevScaleRef.current = scale;
    }, [scale, jumpBarSnapToGrid, isPreview]);
    // --- A11Y: outer wrapper ref + lifecycle aria-label overrides ---
    var outerRef = useRef(null);
    var nlName = t('text.manipulativeNumberLine');
    var duplicateLabel = useDuplicateAnnounce(id, nlName);
    var creationLabel = useCreationAnnounce(id, nlName);
    var announce = useAnnouncer().announce;
    // Menu-opener keyboard handler. Movement / delete live in the existing
    // right-click context menu (ContextMenu.tsx); this wrapper just hands focus
    // off to that menu when the user presses Enter / Space / Shift+F10 /
    // ContextMenu key. Matches DiceManipulative's pattern.
    var onWrapperKeyDown = useCallback(function (e) {
        var _a;
        if (e.target !== e.currentTarget)
            return;
        var isMenuKey = e.key === 'Enter' ||
            e.key === ' ' ||
            e.key === 'ContextMenu' ||
            (e.shiftKey && e.key === 'F10');
        if (!isMenuKey)
            return;
        e.preventDefault();
        e.stopPropagation();
        if (!selectedTool.includes(id))
            setSelectedTool([id]);
        var rect = (_a = outerRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
        if (rect) {
            setContextMenu({ x: rect.right, y: rect.top, manipulativeId: id });
        }
    }, [id, selectedTool, setSelectedTool, setContextMenu]);
    // Local drag state
    var _h = useState({ x: center.x, y: center.y }), position = _h[0], setPosition = _h[1];
    // Transient CSS-only bring-to-front boost while this line (or its linked
    // jumpBar/customTick family) is actively being dragged. The permanent
    // stacking commit happens via array reorder on drop, not via zIndex.
    var _j = useState(false), isDragging = _j[0], setIsDragging = _j[1];
    useEffect(function () {
        var onDragActive = function (ids) { return setIsDragging(ids.includes(id)); };
        customEventHandler.addEventListener('numberLineDragActive', onDragActive);
        return function () {
            customEventHandler.removeEventListener('numberLineDragActive', onDragActive);
        };
    }, [id]);
    var positionRef = useRef({ x: center.x, y: center.y });
    var dragStartRef = useRef(null);
    var dragOffsetRef = useRef({ x: 0, y: 0 });
    var hasMoved = useRef(false);
    var initialCenter = useRef({ x: center.x, y: center.y });
    var manipulativeRef = useRef(manipulative);
    // Multi-select move refs
    var multiSelectStartPositions = useRef([]);
    var selectedToolRef = useRef(selectedTool);
    // Keep refs in sync
    useEffect(function () {
        manipulativeRef.current = manipulative;
    }, [manipulative]);
    useEffect(function () {
        selectedToolRef.current = selectedTool;
    }, [selectedTool]);
    useEffect(function () {
        setPosition({ x: center.x, y: center.y });
        positionRef.current = { x: center.x, y: center.y };
    }, [center.x, center.y]);
    // Compute tick and label positions.
    // When scaling to a manipulative (Fraction Tiles, Colored Tiles), the
    // manipulative dictates a fixed grid-squares-per-unit ratio, so pxPerUnit
    // is invariant of intervalNum — interval only controls tick spacing within
    // that fixed scale. Without scaling, intervalNum stretches each interval
    // step to occupy gridSquaresPerUnit grid squares.
    var pxPerUnit = computePxPerUnit(gridSquaresPerUnit, intervalNum, !!scalingToManipulatives);
    var lineWidth = computeLineWidth(startNum, endNum, gridSquaresPerUnit, intervalNum, !!scalingToManipulatives);
    var ticks = useMemo(function () {
        if (lineMode === 'noTickMarks')
            return [];
        return computeTickPositions(startNum, endNum, intervalNum, pxPerUnit);
    }, [lineMode, startNum, endNum, intervalNum, pxPerUnit]);
    var labels = useMemo(function () {
        var _a, _b, _c;
        if (lineMode === 'noTickMarks')
            return [];
        var nType = (_a = data.numberType) !== null && _a !== void 0 ? _a : 0;
        var startDen = (_b = data.startDenominator) !== null && _b !== void 0 ? _b : 1;
        var intervalDen = (_c = data.intervalDenominator) !== null && _c !== void 0 ? _c : 1;
        // Label-every is a whole-number tick count, so it contributes no
        // denominator. Labeled values are tick values, which depend only on the
        // start and interval denominators — LCM those so each can be represented.
        var lcm = function (a, b) { return (a && b ? (a / gcd(a, b)) * b : a || b || 1); };
        var refDenom = lcm(startDen, intervalDen);
        return computeLabelPositions(ticks, labelIntervalNum, labelIntervalStr, nType, refDenom);
    }, [
        lineMode,
        ticks,
        labelIntervalNum,
        labelIntervalStr,
        data.numberType,
        data.intervalDenominator,
        data.startDenominator,
    ]);
    var totalWidth = lineWidth + ARROWHEAD_SIZE * 2;
    var svgPadding = GRID_SIZE - ARROWHEAD_SIZE;
    var svgWidth = totalWidth + svgPadding * 2;
    var lineStartX = svgPadding + ARROWHEAD_SIZE;
    // --- DRAG-TO-MOVE ---
    var onMouseDown = useCallback(function (e) {
        e.stopPropagation();
        hasMoved.current = false;
        if (panningEnable || positionLock)
            return;
        var clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
        var clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
        dragStartRef.current = { x: clientX, y: clientY };
        dragOffsetRef.current = {
            x: clientX - positionRef.current.x * zoomLevel,
            y: clientY - positionRef.current.y * zoomLevel,
        };
        hasMoved.current = false;
        initialCenter.current = { x: positionRef.current.x, y: positionRef.current.y };
        // Broadcast the whole linked family (this line + its jumpBars/customTicks)
        // as actively dragging, so they all apply the transient CSS boost together.
        var linkedFamilyIds = __spreadArray([
            id
        ], manipulativeRef.current
            .filter(function (m) {
            return m.data.numberLineId === id &&
                (m.data.type === 'jumpBar' || m.data.type === 'customTick');
        })
            .map(function (m) { return m.data.id; }), true);
        customEventHandler.dispatchEvent('numberLineDragActive', linkedFamilyIds);
        // Multi-select: capture start positions of all selected NLs and jump bars.
        // Uses selectedToolRef/manipulativeRef (not the plain selectedTool/manipulative
        // closure vars) to match onMouseMove's gate below — this callback can be a render
        // behind if the number line was just added to the selection in the same gesture,
        // so the plain `selectedTool` here missed it while the ref had already caught up.
        // That mismatch meant 'multiSelectMoveStart' never fired (so the central engine's
        // follower snapshot was never captured) while 'multiSelectMove' fired every frame
        // during the drag — every follower's delta was silently dropped the whole time.
        if (selectedToolRef.current.length > 1 && selectedToolRef.current.includes(id)) {
            multiSelectStartPositions.current = manipulativeRef.current
                .filter(function (item) {
                return item.data.id !== undefined &&
                    selectedToolRef.current.includes(item.data.id) &&
                    (item.data.type === 'numberLine' || item.data.type === 'jumpBar');
            })
                .map(function (item) { return ({
                id: item.data.id,
                center: __assign({}, item.data.center),
            }); });
            customEventHandler.dispatchEvent('multiSelectMoveStart', { sourceId: id });
        }
        var onMouseMove = function (ev) {
            if (!dragStartRef.current)
                return;
            var cx = 'touches' in ev ? ev.touches[0].clientX : ev.clientX;
            var cy = 'touches' in ev ? ev.touches[0].clientY : ev.clientY;
            var dx = cx - dragStartRef.current.x;
            var dy = cy - dragStartRef.current.y;
            if (!hasMoved.current && Math.abs(dx) + Math.abs(dy) < 5)
                return;
            hasMoved.current = true;
            var newX = (cx - dragOffsetRef.current.x) / zoomLevel;
            var newY = (cy - dragOffsetRef.current.y) / zoomLevel;
            var isMultiSelect = selectedToolRef.current.length > 1 && selectedToolRef.current.includes(id);
            // Skip grid snapping in multi-select mode to keep movement synchronized
            if (!isMultiSelect && playerBgPattern === 'square') {
                var topLeftX = newX - svgWidth / 2;
                var topLeftY = newY - TOTAL_SVG_HEIGHT / 2;
                newX = Math.round(topLeftX / GRID_SIZE) * GRID_SIZE + svgWidth / 2;
                newY = Math.round(topLeftY / GRID_SIZE) * GRID_SIZE + TOTAL_SVG_HEIGHT / 2;
            }
            // Move NL center + snapped jump bars by the incremental delta
            var prevPos = positionRef.current;
            var moveDx = newX - prevPos.x;
            var moveDy = newY - prevPos.y;
            if (moveDx !== 0 || moveDy !== 0) {
                setManipulative(function (prev) {
                    var _a;
                    // Read current NL scale from the manipulative array (avoids stale closure)
                    var nlItem = prev.find(function (m) { return m.data.id === id; });
                    var nlScale = ((_a = nlItem === null || nlItem === void 0 ? void 0 : nlItem.data) === null || _a === void 0 ? void 0 : _a.scale) || 1;
                    var lineY = prevPos.y;
                    var SNAP_MARGIN = 15;
                    return prev.map(function (item) {
                        // Keep NL center in sync so child JBs see correct parentLine.center.y
                        if (item.data.id === id) {
                            return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: { x: newX, y: newY } }) });
                        }
                        if (item.data.numberLineId === id && item.data.type === 'jumpBar') {
                            var jb = item.data;
                            // Skip unsnapped JBs — they move independently
                            if (!jb.snappedToLine)
                                return item;
                            // In multi-select, skip JBs that are also selected (they handle their own move)
                            if (isMultiSelect && selectedToolRef.current.includes(item.data.id)) {
                                return item;
                            }
                            // Only move JB if it's visually on the line (line Y within bar bounds + margin)
                            var barH = 30 * nlScale;
                            var barTop = jb.center.y - SNAP_MARGIN;
                            var barBottom = jb.center.y + barH + SNAP_MARGIN;
                            if (lineY >= barTop && lineY <= barBottom) {
                                return __assign(__assign({}, item), { data: __assign(__assign({}, jb), { center: { x: jb.center.x + moveDx, y: jb.center.y + moveDy } }) });
                            }
                        }
                        // Move attached custom ticks with NL
                        if (item.data.numberLineId === id && item.data.type === 'customTick') {
                            if (isMultiSelect && selectedToolRef.current.includes(item.data.id))
                                return item;
                            return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: { x: item.data.center.x + moveDx, y: item.data.center.y + moveDy } }) });
                        }
                        return item;
                    });
                });
            }
            positionRef.current = { x: newX, y: newY };
            // Multi-select: broadcast delta so other selected items follow
            if (isMultiSelect) {
                customEventHandler.dispatchEvent('multiSelectMove', {
                    deltaX: newX - initialCenter.current.x,
                    deltaY: newY - initialCenter.current.y,
                    sourceId: id,
                });
            }
            setPosition({ x: newX, y: newY });
        };
        var onMouseUp = function () {
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
            window.removeEventListener('touchmove', onMouseMove);
            window.removeEventListener('touchend', onMouseUp);
            var isMultiSelectSource = selectedToolRef.current.length > 1 && selectedToolRef.current.includes(id);
            if (hasMoved.current && isMultiSelectSource) {
                // Multi-select: batch saveState for all selected items (single undo step)
                var finalPos = __assign({}, positionRef.current);
                var selfStart = multiSelectStartPositions.current.find(function (p) { return p.id === id; });
                if (selfStart) {
                    var selfDeltaX_1 = finalPos.x - selfStart.center.x;
                    var selfDeltaY_1 = finalPos.y - selfStart.center.y;
                    var batchSave_1 = multiSelectStartPositions.current.map(function (startItem) { return ({
                        type: startItem.id,
                        id: startItem.id.split('_')[1],
                        propertyName: 'center',
                        old: JSON.stringify(startItem.center),
                        new: JSON.stringify({
                            x: startItem.center.x + selfDeltaX_1,
                            y: startItem.center.y + selfDeltaY_1,
                        }),
                    }); });
                    // Same detach-on-leave behavior as NumberLine deletion: any
                    // FractionTile (and its snapped-chain descendants) still attached
                    // to this line no longer sits on its geometry once the line has
                    // moved, so it must lose its NL-forced scale — see
                    // computeFractionTileNumberLineDetachUpdates in snapHelper.ts.
                    // A FractionTile that is itself part of THIS multi-select drag
                    // moved by the same delta as the line (FractionTileManipulative's
                    // own multiSelectMove listener), so it never left the line's
                    // geometry — exclude co-selected tiles from the candidate array
                    // entirely so they're left for that widget's own attach/detach
                    // logic instead of being falsely detached here.
                    var coDraggedIds_1 = new Set(selectedToolRef.current);
                    var detachCandidates = manipulativeRef.current.filter(function (m) { var _a; return m.data.type !== 'fractionTiles' || !coDraggedIds_1.has((_a = m.data.id) !== null && _a !== void 0 ? _a : ''); });
                    var nlDetachUpdates_1 = computeFractionTileNumberLineDetachUpdates(detachCandidates, function (nlId) { return nlId === id; });
                    if (nlDetachUpdates_1.size > 0) {
                        nlDetachUpdates_1.forEach(function (detachedData, tileId) {
                            var _a;
                            var original = manipulativeRef.current.find(function (m) { return m.data.id === tileId; });
                            if (!original)
                                return;
                            batchSave_1.push({
                                type: tileId,
                                id: (_a = tileId.split('_')[1]) !== null && _a !== void 0 ? _a : tileId,
                                propertyName: 'completeData',
                                old: JSON.stringify(original.data),
                                new: JSON.stringify(detachedData),
                            });
                        });
                        setManipulative(function (prev) {
                            return prev.map(function (item) {
                                var _a;
                                var updated = nlDetachUpdates_1.get((_a = item.data.id) !== null && _a !== void 0 ? _a : '');
                                return updated ? __assign(__assign({}, item), { data: updated }) : item;
                            });
                        });
                    }
                    if (!registerMultiSelectSaveEntries(id, batchSave_1)) {
                        saveState(batchSave_1);
                    }
                }
                positionRef.current = finalPos;
                setPosition(finalPos);
                customEventHandler.dispatchEvent('multiSelectMoveEnd', { sourceId: id });
            }
            else if (hasMoved.current) {
                var finalPos_1 = __assign({}, positionRef.current);
                // Grid snap on drop — snap derived top-left so ticks land on grid lines
                var snapDx_1 = 0;
                var snapDy_1 = 0;
                if (playerBgPattern === 'square') {
                    var topLeftX = finalPos_1.x - svgWidth / 2;
                    var topLeftY = finalPos_1.y - TOTAL_SVG_HEIGHT / 2;
                    var snappedX = Math.round(topLeftX / GRID_SIZE) * GRID_SIZE + svgWidth / 2;
                    var snappedY = Math.round(topLeftY / GRID_SIZE) * GRID_SIZE + TOTAL_SVG_HEIGHT / 2;
                    snapDx_1 = snappedX - finalPos_1.x;
                    snapDy_1 = snappedY - finalPos_1.y;
                    finalPos_1.x = snappedX;
                    finalPos_1.y = snappedY;
                }
                // Update manipulative state — commit NL position and apply snap correction to JBs
                setManipulative(function (prev) {
                    var _a;
                    var nlItem = prev.find(function (m) { return m.data.id === id; });
                    var nlScale = ((_a = nlItem === null || nlItem === void 0 ? void 0 : nlItem.data) === null || _a === void 0 ? void 0 : _a.scale) || 1;
                    var SNAP_MARGIN = 15;
                    return prev.map(function (item) {
                        if (item.data.id === id) {
                            return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: { x: finalPos_1.x, y: finalPos_1.y } }) });
                        }
                        // Apply only the final snap correction to snapped JBs (they already moved during drag)
                        if (item.data.numberLineId === id && item.data.type === 'jumpBar') {
                            var jb = item.data;
                            if (!jb.snappedToLine)
                                return item;
                            var barH = 30 * nlScale;
                            var finalLineY = finalPos_1.y;
                            var barTop = jb.center.y - SNAP_MARGIN;
                            var barBottom = jb.center.y + barH + SNAP_MARGIN;
                            var isOnLine = finalLineY >= barTop && finalLineY <= barBottom;
                            if (isOnLine && (snapDx_1 !== 0 || snapDy_1 !== 0)) {
                                return __assign(__assign({}, item), { data: __assign(__assign({}, jb), { center: { x: jb.center.x + snapDx_1, y: jb.center.y + snapDy_1 } }) });
                            }
                        }
                        // Apply snap correction to custom ticks
                        if (item.data.numberLineId === id && item.data.type === 'customTick') {
                            if (snapDx_1 !== 0 || snapDy_1 !== 0) {
                                return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: { x: item.data.center.x + snapDx_1, y: item.data.center.y + snapDy_1 } }) });
                            }
                        }
                        return item;
                    });
                });
                positionRef.current = finalPos_1;
                setPosition(finalPos_1);
                // Bring-to-front: move this line + its linked jumpBars/customTicks
                // to the end of the array (base-first, so children land later =
                // frontmost), matching the pattern used by other manipulatives.
                var linkedFamilyIds_1 = __spreadArray([
                    id
                ], manipulativeRef.current
                    .filter(function (m) {
                    return m.data.numberLineId === id &&
                        (m.data.type === 'jumpBar' || m.data.type === 'customTick');
                })
                    .map(function (m) { return m.data.id; }), true);
                var beforeIds = manipulativeRef.current.map(function (m) { var _a; return (_a = m.data.id) !== null && _a !== void 0 ? _a : ''; });
                var alreadyAtFront = beforeIds
                    .slice(-linkedFamilyIds_1.length)
                    .every(function (mid, i) { return mid === linkedFamilyIds_1[i]; });
                var batchEntries_1 = [
                    {
                        type: id,
                        id: id.split('_')[1],
                        propertyName: 'center',
                        old: JSON.stringify(initialCenter.current),
                        new: JSON.stringify(finalPos_1),
                    },
                ];
                if (!alreadyAtFront) {
                    // Reorder via a functional update against the live `prev`, not a
                    // replacement built from manipulativeRef.current — the snap
                    // correction above already queued its own functional
                    // setManipulative call; replacing state with ref-built objects
                    // here would silently clobber that fresh position data.
                    var afterIds = __spreadArray(__spreadArray([], beforeIds.filter(function (bid) { return !linkedFamilyIds_1.includes(bid); }), true), linkedFamilyIds_1, true);
                    batchEntries_1.push({
                        type: id,
                        id: id.split('_')[1],
                        propertyName: 'reorder',
                        old: JSON.stringify(beforeIds),
                        new: JSON.stringify(afterIds),
                    });
                    setManipulative(function (prev) { return bringManipulativesToFront(prev, linkedFamilyIds_1); });
                }
                // Same detach-on-leave behavior as NumberLine deletion: any
                // FractionTile (and its snapped-chain descendants) still attached
                // to this line no longer sits on its geometry once the line has
                // moved, so it must lose its NL-forced scale — see
                // computeFractionTileNumberLineDetachUpdates in snapHelper.ts.
                var nlDetachUpdates_2 = computeFractionTileNumberLineDetachUpdates(manipulativeRef.current, function (nlId) { return nlId === id; });
                if (nlDetachUpdates_2.size > 0) {
                    nlDetachUpdates_2.forEach(function (detachedData, tileId) {
                        var _a;
                        var original = manipulativeRef.current.find(function (m) { return m.data.id === tileId; });
                        if (!original)
                            return;
                        batchEntries_1.push({
                            type: tileId,
                            id: (_a = tileId.split('_')[1]) !== null && _a !== void 0 ? _a : tileId,
                            propertyName: 'completeData',
                            old: JSON.stringify(original.data),
                            new: JSON.stringify(detachedData),
                        });
                    });
                    setManipulative(function (prev) {
                        return prev.map(function (item) {
                            var _a;
                            var updated = nlDetachUpdates_2.get((_a = item.data.id) !== null && _a !== void 0 ? _a : '');
                            return updated ? __assign(__assign({}, item), { data: updated }) : item;
                        });
                    });
                }
                saveState(batchEntries_1);
            }
            dragStartRef.current = null;
            customEventHandler.dispatchEvent('numberLineDragActive', []);
        };
        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);
        window.addEventListener('touchmove', onMouseMove);
        window.addEventListener('touchend', onMouseUp);
    }, [panningEnable, positionLock, zoomLevel, id, setManipulative, playerBgPattern, svgWidth]);
    // --- Multi-select listener: respond when another selected item is dragged ---
    useEffect(function () {
        if (!selectedTool.includes(id) || selectedTool.length <= 1)
            return;
        var myStart = { x: positionRef.current.x, y: positionRef.current.y };
        var onStart = function (_a) {
            var sourceId = _a.sourceId;
            if (sourceId === id)
                return;
            myStart = { x: positionRef.current.x, y: positionRef.current.y };
        };
        var onMove = function (_a) {
            var deltaX = _a.deltaX, deltaY = _a.deltaY, sourceId = _a.sourceId;
            if (sourceId === id || positionLock)
                return;
            var newX = myStart.x + deltaX;
            var newY = myStart.y + deltaY;
            var moveDx = newX - positionRef.current.x;
            var moveDy = newY - positionRef.current.y;
            positionRef.current = { x: newX, y: newY };
            setPosition({ x: newX, y: newY });
            // Also move non-selected attached jump bars (they rely on NL drag to follow)
            if (moveDx !== 0 || moveDy !== 0) {
                setManipulative(function (prev) {
                    var _a;
                    var nlItem = prev.find(function (m) { return m.data.id === id; });
                    var nlScale = ((_a = nlItem === null || nlItem === void 0 ? void 0 : nlItem.data) === null || _a === void 0 ? void 0 : _a.scale) || 1;
                    var SNAP_MARGIN = 15;
                    return prev.map(function (item) {
                        if (item.data.id === id) {
                            return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: { x: newX, y: newY } }) });
                        }
                        if (item.data.numberLineId === id && item.data.type === 'jumpBar') {
                            var jb = item.data;
                            // Skip unsnapped JBs — they move independently
                            if (!jb.snappedToLine)
                                return item;
                            // Skip JBs that are also selected — they handle their own move
                            if (selectedToolRef.current.includes(item.data.id))
                                return item;
                            var barH = 30 * nlScale;
                            var barTop = jb.center.y - SNAP_MARGIN;
                            var barBottom = jb.center.y + barH + SNAP_MARGIN;
                            var lineY = positionRef.current.y;
                            if (lineY >= barTop && lineY <= barBottom) {
                                return __assign(__assign({}, item), { data: __assign(__assign({}, jb), { center: { x: jb.center.x + moveDx, y: jb.center.y + moveDy } }) });
                            }
                        }
                        // Move attached custom ticks
                        if (item.data.numberLineId === id && item.data.type === 'customTick') {
                            if (selectedToolRef.current.includes(item.data.id))
                                return item;
                            return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: { x: item.data.center.x + moveDx, y: item.data.center.y + moveDy } }) });
                        }
                        return item;
                    });
                });
            }
        };
        customEventHandler.addEventListener('multiSelectMoveStart', onStart);
        customEventHandler.addEventListener('multiSelectMove', onMove);
        return function () {
            customEventHandler.removeEventListener('multiSelectMoveStart', onStart);
            customEventHandler.removeEventListener('multiSelectMove', onMove);
        };
        // positionRef keeps position current without putting it in deps (avoids mid-drag re-runs)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTool, id, positionLock, setManipulative]);
    // --- CLICK TO SELECT ---
    var onClick = useCallback(function (e) {
        if (hasMoved.current)
            return;
        e.stopPropagation();
        if (e.ctrlKey || e.metaKey || e.shiftKey) {
            // Multi-select toggle
            if (isFocused) {
                setSelectedTool(selectedTool.filter(function (t) { return t !== id; }));
            }
            else {
                setSelectedTool(__spreadArray(__spreadArray([], selectedTool, true), [id], false));
            }
        }
        else {
            setSelectedTool([id]);
        }
    }, [id, isFocused, selectedTool, setSelectedTool]);
    // --- END-EXTENSION (left/right drag handles) ---
    var onEndDrag = useCallback(function (side, e) {
        if (lock === null || lock === void 0 ? void 0 : lock.editing)
            return;
        e.stopPropagation();
        e.preventDefault();
        var startClientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
        var origStartNum = startNum;
        var origEndNum = endNum;
        var origCenterX = positionRef.current.x;
        var origSvgWidth = (origEndNum - origStartNum) * pxPerUnit + 2 * GRID_SIZE;
        var onMove = function (ev) {
            var clientX = 'touches' in ev ? ev.touches[0].clientX : ev.clientX;
            var deltaClientX = clientX - startClientX;
            var deltaUnits = deltaClientX / (zoomLevel * (scale || 1) * pxPerUnit);
            // Quantize to intervalNum steps
            var quantizedDelta = Math.round(deltaUnits / intervalNum) * intervalNum;
            if (side === 'right') {
                var newEnd_1 = origEndNum + quantizedDelta;
                var newRange = newEnd_1 - origStartNum;
                // Enforce minimum range so arrowheads don't overlap
                if (newRange < intervalNum)
                    return;
                // Enforce MAX_UNITS limit
                if (intervalNum > 0 && (newRange / intervalNum) * gridSquaresPerUnit > MAX_UNITS)
                    return;
                if (newEnd_1 > origStartNum) {
                    var newLineWidth_1 = (newEnd_1 - origStartNum) * pxPerUnit + ARROWHEAD_SIZE * 2;
                    // Center shifts: visual left edge is anchored (must account for scale)
                    var newSvgWidth = (newEnd_1 - origStartNum) * pxPerUnit + 2 * GRID_SIZE;
                    var newCX_1 = origCenterX + ((newSvgWidth - origSvgWidth) * (scale || 1)) / 2;
                    positionRef.current = { x: newCX_1, y: positionRef.current.y };
                    setPosition({ x: newCX_1, y: positionRef.current.y });
                    setManipulative(function (prev) {
                        return prev.map(function (item) {
                            var _a;
                            if (item.data.id === id) {
                                return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { endNum: parseFloat(newEnd_1.toFixed(6)), endValue: String(parseFloat(newEnd_1.toFixed(6))), endNumerator: parseFloat(newEnd_1.toFixed(6)) * ((_a = item.data.endDenominator) !== null && _a !== void 0 ? _a : 1), center: { x: newCX_1, y: item.data.center.y }, dimensions: { width: newLineWidth_1, height: dimensions.height } }) });
                            }
                            return item;
                        });
                    });
                }
            }
            else {
                var newStart_1 = origStartNum + quantizedDelta;
                var newRange = origEndNum - newStart_1;
                // Enforce minimum range so arrowheads don't overlap
                if (newRange < intervalNum)
                    return;
                // Enforce MAX_UNITS limit
                if (intervalNum > 0 && (newRange / intervalNum) * gridSquaresPerUnit > MAX_UNITS)
                    return;
                if (newStart_1 < origEndNum) {
                    var newLineWidth_2 = (origEndNum - newStart_1) * pxPerUnit + ARROWHEAD_SIZE * 2;
                    // Center shifts: visual right edge is anchored (must account for scale)
                    var newSvgWidth = (origEndNum - newStart_1) * pxPerUnit + 2 * GRID_SIZE;
                    var newCX_2 = origCenterX - ((newSvgWidth - origSvgWidth) * (scale || 1)) / 2;
                    positionRef.current = { x: newCX_2, y: positionRef.current.y };
                    setPosition({ x: newCX_2, y: positionRef.current.y });
                    setManipulative(function (prev) {
                        return prev.map(function (item) {
                            var _a;
                            if (item.data.id === id) {
                                return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { startNum: parseFloat(newStart_1.toFixed(6)), startValue: String(parseFloat(newStart_1.toFixed(6))), startNumerator: parseFloat(newStart_1.toFixed(6)) * ((_a = item.data.startDenominator) !== null && _a !== void 0 ? _a : 1), center: { x: newCX_2, y: item.data.center.y }, dimensions: { width: newLineWidth_2, height: dimensions.height } }) });
                            }
                            return item;
                        });
                    });
                }
            }
        };
        var onUp = function () {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            window.removeEventListener('touchmove', onMove);
            window.removeEventListener('touchend', onUp);
            // Save state for undo
            var currentItem = manipulativeRef.current.find(function (m) { return m.data.id === id; });
            if (currentItem) {
                saveState({
                    type: id,
                    id: id.split('_')[1],
                    propertyName: 'completeData',
                    old: JSON.stringify(__assign(__assign({}, currentItem), { data: __assign(__assign({}, currentItem.data), { startNum: origStartNum, endNum: origEndNum, center: { x: origCenterX, y: center.y } }) })),
                    new: JSON.stringify(currentItem),
                });
            }
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        window.addEventListener('touchmove', onMove);
        window.addEventListener('touchend', onUp);
    }, [
        lock,
        startNum,
        endNum,
        zoomLevel,
        scale,
        pxPerUnit,
        intervalNum,
        id,
        setManipulative,
        dimensions,
        center,
    ]);
    // --- BOTTOM PANEL EVENTS ---
    useEffect(function () {
        if (isPreview)
            return;
        var onToolClick = function (evt) {
            // The right panel's NL toolbar fires when either the NL itself is
            // selected OR one of its child jump bars is selected. The JB-only
            // path lets the user click a JB and still use Add Jump Bar / Add
            // Tick / etc. on its parent line. Only honour the JB path when no
            // NL is directly selected — otherwise the directly-selected NL
            // owns the event.
            var directNlSelected = selectedToolRef.current.some(function (sel) {
                return manipulativeRef.current.some(function (m) { return m.data.id === sel && m.data.type === 'numberLine'; });
            });
            var jbForThisNlSelected = selectedToolRef.current.some(function (sel) {
                var m = manipulativeRef.current.find(function (mm) { return mm.data.id === sel && mm.data.type === 'jumpBar'; });
                return m && m.data.numberLineId === id;
            });
            if (!isFocused && !(jbForThisNlSelected && !directNlSelected))
                return;
            // Guard against multi-select / stale-listener races: when more than
            // one NumberLine is in `selectedTool`, only the FIRST selected NL
            // acts on toolbar events that create line-anchored manipulatives
            // (jump bars, custom ticks). Otherwise both NLs' listeners fire and
            // the new JB / tick gets attached to whichever NL handled it first
            // (typically the older one), producing the "I selected NL2 but the
            // jump bar appeared on NL1" bug.
            var requiresUniqueOwner = evt.id === 'jumpBar' || evt.id === 'customTickMark';
            if (requiresUniqueOwner) {
                // Read from refs so a stale-closure selectedTool / manipulative
                // from a render before the most recent selection change can't
                // mis-attribute the action.
                var firstSelectedNlId = selectedToolRef.current.find(function (sel) {
                    return manipulativeRef.current.some(function (m) { return m.data.id === sel && m.data.type === 'numberLine'; });
                });
                if (firstSelectedNlId && firstSelectedNlId !== id)
                    return;
            }
            switch (evt.id) {
                case 'edit':
                case 'configure':
                    setSelectedManipulativeId(id);
                    setManiplulatorComponentVisible(true);
                    setActiveManipulator('numberLines');
                    break;
                case 'scalePlus': {
                    var oldScale_1 = scale || 1;
                    var newScale_1 = oldScale_1 + 0.1;
                    var halfLineW_1 = ((endNum - startNum) * pxPerUnit) / 2;
                    saveState({
                        type: id,
                        id: id.split('_')[1],
                        propertyName: 'scale',
                        old: JSON.stringify(oldScale_1),
                        new: JSON.stringify(newScale_1),
                    });
                    setManipulative(function (prev) {
                        return prev.map(function (item) {
                            if (item.data.id === id)
                                return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { scale: newScale_1 }) });
                            if (item.data.numberLineId === id && item.data.type === 'jumpBar') {
                                var jb = item.data;
                                var JUMP_BAR_H = 30;
                                var barHOld = JUMP_BAR_H * oldScale_1;
                                var barCenterYOld = jb.center.y + barHOld / 2;
                                var isOnLine = Math.abs(barCenterYOld - position.y) < barHOld;
                                if (!isOnLine) {
                                    return __assign(__assign({}, item), { data: __assign(__assign({}, jb), { scale: newScale_1 }) });
                                }
                                var lineStartOld = position.x - halfLineW_1 * oldScale_1;
                                var logicalPos = startNum + (jb.center.x - lineStartOld) / (pxPerUnit * oldScale_1);
                                var lineStartNew = position.x - halfLineW_1 * newScale_1;
                                var newX = lineStartNew + (logicalPos - startNum) * pxPerUnit * newScale_1;
                                // Adjust Y to maintain snap alignment: visual bar height changes with scale
                                var visualHOld = JUMP_BAR_H * oldScale_1;
                                var visualHNew = JUMP_BAR_H * newScale_1;
                                var anchorFraction = (position.y - jb.center.y) / visualHOld;
                                var newY = position.y - anchorFraction * visualHNew;
                                return __assign(__assign({}, item), { data: __assign(__assign({}, jb), { scale: newScale_1, center: { x: newX, y: newY }, snappedPosition: parseFloat(logicalPos.toFixed(6)) }) });
                            }
                            return item;
                        });
                    });
                    break;
                }
                case 'scaleMinus': {
                    var oldScale_2 = scale || 1;
                    var newScale_2 = Math.max(0.5, oldScale_2 - 0.1);
                    var halfLineWMinus_1 = ((endNum - startNum) * pxPerUnit) / 2;
                    saveState({
                        type: id,
                        id: id.split('_')[1],
                        propertyName: 'scale',
                        old: JSON.stringify(oldScale_2),
                        new: JSON.stringify(newScale_2),
                    });
                    setManipulative(function (prev) {
                        return prev.map(function (item) {
                            if (item.data.id === id)
                                return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { scale: newScale_2 }) });
                            if (item.data.numberLineId === id && item.data.type === 'jumpBar') {
                                var jb = item.data;
                                var JUMP_BAR_H = 30;
                                var barHOld = JUMP_BAR_H * oldScale_2;
                                var barCenterYOld = jb.center.y + barHOld / 2;
                                var isOnLine = Math.abs(barCenterYOld - position.y) < barHOld;
                                if (!isOnLine) {
                                    return __assign(__assign({}, item), { data: __assign(__assign({}, jb), { scale: newScale_2 }) });
                                }
                                var lineStartOld = position.x - halfLineWMinus_1 * oldScale_2;
                                var logicalPos = startNum + (jb.center.x - lineStartOld) / (pxPerUnit * oldScale_2);
                                var lineStartNew = position.x - halfLineWMinus_1 * newScale_2;
                                var newX = lineStartNew + (logicalPos - startNum) * pxPerUnit * newScale_2;
                                // Adjust Y to maintain snap alignment
                                var visualHOld = JUMP_BAR_H * oldScale_2;
                                var visualHNew = JUMP_BAR_H * newScale_2;
                                var anchorFraction = (position.y - jb.center.y) / visualHOld;
                                var newY = position.y - anchorFraction * visualHNew;
                                return __assign(__assign({}, item), { data: __assign(__assign({}, jb), { scale: newScale_2, center: { x: newX, y: newY }, snappedPosition: parseFloat(logicalPos.toFixed(6)) }) });
                            }
                            return item;
                        });
                    });
                    break;
                }
                case 'lineMasking': {
                    // Toggle label mask. Announce the new state so SR users get
                    // confirmation that the action took effect (otherwise the
                    // visual change is invisible).
                    var currentItem = manipulativeRef.current.find(function (m) { return m.data.id === id; });
                    if (!currentItem)
                        break;
                    var nlData = currentItem.data;
                    var willBeMasked = nlData.labelsVisible; // we're flipping → if visible now, will be masked
                    var oldMaskState = {
                        labelsVisible: nlData.labelsVisible,
                        unmaskedLabels: nlData.unmaskedLabels,
                    };
                    var newMaskState_1 = {
                        labelsVisible: !nlData.labelsVisible,
                        unmaskedLabels: nlData.labelsVisible ? [] : nlData.unmaskedLabels,
                    };
                    saveState({
                        type: id,
                        id: id.split('_')[1],
                        propertyName: 'completeData',
                        old: JSON.stringify(oldMaskState),
                        new: JSON.stringify(newMaskState_1),
                    });
                    setManipulative(function (prev) {
                        return prev.map(function (item) {
                            if (item.data.id === id) {
                                return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), newMaskState_1) });
                            }
                            return item;
                        });
                    });
                    announce(willBeMasked
                        ? t('text.numberLineLabelsMasked')
                        : t('text.numberLineLabelsUnmasked'));
                    break;
                }
                case 'maskAll': {
                    // Re-add every mask at once instead of clicking each revealed
                    // label individually. Only meaningful while masking mode is on.
                    var currentItem = manipulativeRef.current.find(function (m) { return m.data.id === id; });
                    if (!currentItem)
                        break;
                    var nlData = currentItem.data;
                    if (nlData.labelsVisible || nlData.unmaskedLabels.length === 0)
                        break;
                    var oldUnmasked = nlData.unmaskedLabels;
                    saveState({
                        type: id,
                        id: id.split('_')[1],
                        propertyName: 'unmaskedLabels',
                        old: JSON.stringify(oldUnmasked),
                        new: JSON.stringify([]),
                    });
                    setManipulative(function (prev) {
                        return prev.map(function (item) {
                            if (item.data.id === id) {
                                return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { unmaskedLabels: [] }) });
                            }
                            return item;
                        });
                    });
                    announce(t('text.numberLineLabelsMasked'));
                    break;
                }
                case 'jumpLabel': {
                    // Feature 4: Toggle showNumericalValue on all attached jump bars
                    var attachedBars = manipulative.filter(function (m) { return m.data.numberLineId === id; });
                    if (attachedBars.length === 0)
                        break;
                    var anyShowing = attachedBars.some(function (m) { return m.data.showNumericalValue; });
                    var newValue_1 = !anyShowing;
                    setManipulative(function (prev) {
                        return prev.map(function (item) {
                            if (item.data.numberLineId === id && item.data.type === 'jumpBar') {
                                return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { showNumericalValue: newValue_1 }) });
                            }
                            return item;
                        });
                    });
                    // Announce so SR users hear that the action toggled the
                    // numerical labels on attached jump bars.
                    announce(newValue_1
                        ? t('text.jumpBarLabelsShown')
                        : t('text.jumpBarLabelsHidden'));
                    break;
                }
                case 'jumpBar': {
                    // JBs attached to THIS NL — used to anchor placement on the
                    // selected line. Earlier this code used the GLOBAL last-placed
                    // jump bar across all canvases; that meant adding a jump bar to
                    // a freshly-selected number line could land it next to a jump
                    // bar belonging to a different line. The user-facing rule is
                    // simpler: every new jump bar appears on the line it was added
                    // to. The first JB on a line floats above its midpoint; each
                    // subsequent JB on the same line goes flush right of the
                    // last-placed JB on that same line.
                    var siblingsOnThisNL = manipulative
                        .filter(function (m) { return m.data.numberLineId === id && m.data.type === 'jumpBar'; })
                        .map(function (m) { return m.data; });
                    var lastBarOnThisNL = siblingsOnThisNL.length > 0 ? siblingsOnThisNL[siblingsOnThisNL.length - 1] : null;
                    var isFirstForThisNL = siblingsOnThisNL.length === 0;
                    var currentScale = scale || 1;
                    var range = endNum - startNum;
                    var defaultGridSquares = Math.min(DEFAULT_JUMP_BAR_LENGTH, range * gridSquaresPerUnit);
                    var barWidth = lastBarOnThisNL ? lastBarOnThisNL.width : defaultGridSquares * GRID_SIZE;
                    var barDirection = lastBarOnThisNL ? lastBarOnThisNL.direction : 'forward';
                    var inheritShowNumerical = siblingsOnThisNL.some(function (jb) { return jb.showNumericalValue; });
                    // Compute ABSOLUTE position (accounting for NL scale)
                    var newX = void 0;
                    var newY = void 0;
                    // NL coordinate of the new bar's start edge, derived from its
                    // flush-right placement. Only set when the new bar inherits an
                    // on-line position from the previous (snapped) bar — a floating
                    // first/off-line bar stays null until the user drags it on.
                    var inheritedSnappedPosition = null;
                    if (isFirstForThisNL || !lastBarOnThisNL) {
                        // First JB for this NL — float ~2 grid squares above the line so
                        // the user can see it and drag it onto the desired tick. Bar
                        // bottom edge sits 2 grid squares above the line.
                        var lineW = (endNum - startNum) * pxPerUnit;
                        newX = position.x - (lineW / 2) * currentScale;
                        newY = position.y - 2 * GRID_SIZE - 30 * currentScale;
                    }
                    else {
                        // Subsequent JBs on THIS NL — flush right of the visual right
                        // edge of the most-recent JB on this line. Anchored to the
                        // selected line, not to whatever JB happens to have been added
                        // last on the canvas.
                        var lastScale = lastBarOnThisNL.scale || 1;
                        newY = lastBarOnThisNL.center.y;
                        newX = lastBarOnThisNL.center.x + lastBarOnThisNL.width * lastScale;
                        // When the previous bar is already snapped to the line, the new
                        // bar lands flush-right ON the line too — give it a snappedPosition
                        // so its auto (numerical) label renders. Without this the whole
                        // chain after the first dragged-on bar stays snappedPosition:null
                        // and shows no length label. Mirrors the drag/resize onUp formula
                        // in NumberLineJumpBar: NL coord of the bar's left (start) edge.
                        var lastSnapped = lastBarOnThisNL.snappedPosition !== null &&
                            lastBarOnThisNL.snappedPosition !== undefined;
                        if (lastSnapped && pxPerUnit > 0) {
                            var halfLineW = ((endNum - startNum) * pxPerUnit) / 2;
                            var lineStartVisual = position.x - halfLineW * currentScale;
                            inheritedSnappedPosition = parseFloat((startNum +
                                (newX - lineStartVisual) / (pxPerUnit * currentScale)).toFixed(6));
                        }
                    }
                    var newJumpBarId = "jumpBar_".concat(uuidv4().replace(/-/g, ''));
                    var newJumpBar_1 = {
                        type: 'jumpBar',
                        data: {
                            id: newJumpBarId,
                            type: 'jumpBar',
                            numberLineId: id,
                            center: { x: newX, y: newY },
                            dimensions: { width: barWidth, height: 60 },
                            width: barWidth,
                            unitLength: barWidth / pxPerUnit,
                            direction: barDirection,
                            text: [{ type: 'text', value: '' }],
                            showNumericalValue: inheritShowNumerical,
                            snappedToLine: jumpBarSnapToGrid,
                            // A first JB (floating above the line) or one placed beside an
                            // off-line bar stays null until the user drags it onto the NL.
                            // A subsequent JB placed flush-right of an already-snapped bar
                            // inherits an on-line snappedPosition so its auto label renders.
                            snappedPosition: inheritedSnappedPosition,
                            scale: currentScale,
                            rotation: 0,
                            lock: { positioning: false, editing: false, length: false },
                        },
                    };
                    setManipulative(function (prev) { return __spreadArray(__spreadArray([], prev, true), [newJumpBar_1], false); });
                    saveState({
                        type: newJumpBarId,
                        id: newJumpBarId.split('_')[1],
                        propertyName: 'create',
                        old: JSON.stringify(newJumpBar_1),
                    });
                    break;
                }
                case 'customTickMark': {
                    // Find existing custom ticks attached to this NL
                    var existingCustomTicks = manipulativeRef.current.filter(function (m) { return m.data.type === 'customTick' && m.data.numberLineId === id; });
                    var tickPosition_1 = (startNum + endNum) / 2;
                    // 15px spacing in units (between 10-20px)
                    var spacingUnits_1 = pxPerUnit > 0 ? 15 / pxPerUnit : 0.1;
                    // Offset if a custom tick already exists too close
                    while (existingCustomTicks.some(function (ct) { return Math.abs(ct.data.position - tickPosition_1) < spacingUnits_1; }) &&
                        tickPosition_1 + spacingUnits_1 <= endNum) {
                        tickPosition_1 += spacingUnits_1;
                    }
                    // If no room to the right, try left
                    if (existingCustomTicks.some(function (ct) { return Math.abs(ct.data.position - tickPosition_1) < spacingUnits_1; })) {
                        tickPosition_1 = (startNum + endNum) / 2;
                        while (existingCustomTicks.some(function (ct) { return Math.abs(ct.data.position - tickPosition_1) < spacingUnits_1; }) &&
                            tickPosition_1 - spacingUnits_1 >= startNum) {
                            tickPosition_1 -= spacingUnits_1;
                        }
                    }
                    var lineW = (endNum - startNum) * pxPerUnit;
                    var ctPxFromStart = (tickPosition_1 - startNum) * pxPerUnit;
                    var currentScale = scale || 1;
                    var ctWorldX = positionRef.current.x - (lineW / 2) * currentScale + ctPxFromStart * currentScale;
                    var ctWorldY = positionRef.current.y;
                    var newTickId = "customTick_".concat(uuidv4().replace(/-/g, ''));
                    var newCustomTick_1 = {
                        type: 'customTick',
                        data: {
                            id: newTickId,
                            type: 'customTick',
                            numberLineId: id,
                            center: { x: ctWorldX, y: ctWorldY },
                            position: parseFloat(tickPosition_1.toFixed(6)),
                            label: [{ type: 'text', value: '' }],
                            isAbove: false,
                            scale: 1,
                            rotation: 0,
                            lock: { positioning: false, editing: false },
                        },
                    };
                    setManipulative(function (prev) { return __spreadArray(__spreadArray([], prev, true), [newCustomTick_1], false); });
                    // The `create` undo handler in stateManagerHelper does
                    // `JSON.parse(old)` to recover the manipulative ID, so passing an
                    // empty string crashes the parse and silently swallows the undo.
                    // Pass the full new manipulative as `old` so undo round-trips.
                    saveState({
                        type: newTickId,
                        id: newTickId.split('_')[1],
                        propertyName: 'create',
                        old: JSON.stringify(newCustomTick_1),
                        new: JSON.stringify(newCustomTick_1),
                    });
                    break;
                }
                case 'nlExtendLeft':
                case 'nlShrinkLeft':
                case 'nlExtendRight':
                case 'nlShrinkRight': {
                    var currentItem = manipulativeRef.current.find(function (m) { return m.data.id === id; });
                    if (!currentItem || (lock === null || lock === void 0 ? void 0 : lock.editing))
                        break;
                    var nlData = currentItem.data;
                    var curStart = nlData.startNum;
                    var curEnd = nlData.endNum;
                    var curCX = nlData.center.x;
                    var step = intervalNum;
                    var newStart_2 = curStart;
                    var newEnd_2 = curEnd;
                    if (evt.id === 'nlExtendLeft')
                        newStart_2 = curStart - step;
                    else if (evt.id === 'nlShrinkLeft')
                        newStart_2 = curStart + step;
                    else if (evt.id === 'nlExtendRight')
                        newEnd_2 = curEnd + step;
                    else if (evt.id === 'nlShrinkRight')
                        newEnd_2 = curEnd - step;
                    var newRange = newEnd_2 - newStart_2;
                    if (newRange < intervalNum)
                        break;
                    if (intervalNum > 0 && (newRange / intervalNum) * gridSquaresPerUnit > MAX_UNITS)
                        break;
                    if (newEnd_2 <= newStart_2)
                        break;
                    var origSvgWidth = (curEnd - curStart) * pxPerUnit;
                    var newSvgWidth = newRange * pxPerUnit;
                    var newLineWidth_3 = newRange * pxPerUnit + ARROWHEAD_SIZE * 2;
                    var newCX_3 = curCX;
                    if (evt.id === 'nlExtendLeft' || evt.id === 'nlShrinkLeft') {
                        // right edge anchored
                        newCX_3 = curCX - ((newSvgWidth - origSvgWidth) * (scale || 1)) / 2;
                    }
                    else {
                        // left edge anchored
                        newCX_3 = curCX + ((newSvgWidth - origSvgWidth) * (scale || 1)) / 2;
                    }
                    saveState({
                        type: id,
                        id: id.split('_')[1],
                        propertyName: 'completeData',
                        old: JSON.stringify(currentItem),
                        new: JSON.stringify(__assign(__assign({}, currentItem), { data: __assign(__assign({}, nlData), { startNum: parseFloat(newStart_2.toFixed(6)), startValue: String(parseFloat(newStart_2.toFixed(6))), endNum: parseFloat(newEnd_2.toFixed(6)), endValue: String(parseFloat(newEnd_2.toFixed(6))), center: { x: newCX_3, y: nlData.center.y }, dimensions: { width: newLineWidth_3, height: dimensions.height } }) })),
                    });
                    announce(t('text.numberLineExtended', { start: newStart_2, end: newEnd_2 }));
                    positionRef.current = { x: newCX_3, y: positionRef.current.y };
                    setPosition({ x: newCX_3, y: positionRef.current.y });
                    setManipulative(function (prev) {
                        return prev.map(function (item) {
                            var _a, _b;
                            if (item.data.id !== id)
                                return item;
                            return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { startNum: parseFloat(newStart_2.toFixed(6)), startValue: String(parseFloat(newStart_2.toFixed(6))), startNumerator: parseFloat(newStart_2.toFixed(6)) * ((_a = item.data.startDenominator) !== null && _a !== void 0 ? _a : 1), endNum: parseFloat(newEnd_2.toFixed(6)), endValue: String(parseFloat(newEnd_2.toFixed(6))), endNumerator: parseFloat(newEnd_2.toFixed(6)) * ((_b = item.data.endDenominator) !== null && _b !== void 0 ? _b : 1), center: { x: newCX_3, y: item.data.center.y }, dimensions: { width: newLineWidth_3, height: dimensions.height } }) });
                        });
                    });
                    break;
                }
                default:
                    break;
            }
        };
        customEventHandler.addEventListener('bottomPanel', onToolClick);
        return function () { return customEventHandler.removeEventListener('bottomPanel', onToolClick); };
    }, [
        isFocused,
        id,
        scale,
        data,
        pxPerUnit,
        lineStartX,
        startNum,
        endNum,
        center,
        lock,
        setManipulative,
        setSelectedTool,
        setSelectedManipulativeId,
        setManiplulatorComponentVisible,
        setActiveManipulator,
        manipulative,
        intervalNum,
        gridSquaresPerUnit,
        dimensions,
    ]);
    // --- UNMASK INDIVIDUAL LABEL ---
    var handleMaskClick = useCallback(function (labelValue) {
        if (labelsVisible)
            return; // Labels are visible, no mask to click
        var currentItem = manipulativeRef.current.find(function (m) { return m.data.id === id; });
        if (!currentItem)
            return;
        var nlData = currentItem.data;
        var oldUnmasked = nlData.unmaskedLabels;
        var newUnmasked = oldUnmasked.includes(labelValue)
            ? oldUnmasked.filter(function (v) { return v !== labelValue; })
            : __spreadArray(__spreadArray([], oldUnmasked, true), [labelValue], false);
        saveState({
            type: id,
            id: id.split('_')[1],
            propertyName: 'unmaskedLabels',
            old: JSON.stringify(oldUnmasked),
            new: JSON.stringify(newUnmasked),
        });
        setManipulative(function (prev) {
            return prev.map(function (item) {
                if (item.data.id === id) {
                    return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { unmaskedLabels: newUnmasked }) });
                }
                return item;
            });
        });
    }, [id, labelsVisible, setManipulative]);
    return (_jsxs("div", { ref: outerRef, className: "numberLineWidget ".concat(isFocused ? 'selected' : '', " ").concat(positionLock ? 'locked' : ''), "data-manipulative-id": data.id, style: {
            position: isPreview ? 'relative' : 'absolute',
            left: isPreview ? undefined : position.x,
            top: isPreview ? undefined : position.y,
            transform: isPreview ? 'none' : "translate(-50%, -50%) scale(".concat(scale, ")"),
            // CSS-only bring-to-front while actively dragging — the permanent
            // stacking order comes from the `manipulative` array position
            // (reordered + saveState-tracked on drop), not from zIndex.
            zIndex: isDragging ? 9999 : undefined,
        }, onMouseDown: isPreview ? undefined : onMouseDown, onTouchStart: isPreview ? undefined : onMouseDown, onClick: isPreview ? undefined : onClick, 
        // --- WCAG 2.2 a11y ---
        // Make the number line a single keyboard tab stop. Selection +
        // contextual actions (Move N/S/E/W, Delete) happen through the
        // right-click context menu that Enter / Space / Shift+F10 / ContextMenu
        // key surfaces — same pattern as DiceManipulative. The preview
        // (toolbar drag-preview) is not interactive and stays out of the tab
        // order.
        // role="group" (NOT "button") because the wrapper contains an
        // interactive descendant — the kebab "Open options menu" button
        // — and ARIA forbids interactive descendants of buttons. With
        // role="button", VoiceOver on touch collapses the wrapper to
        // just its accessible name and swipe-right never reaches the
        // kebab, so the user can't open the context menu on iPad. Group
        // permits children to remain navigable while aria-label still
        // names the whole number line as a unit; the wrapper's onKeyDown
        // (Enter → menu) still works because role doesn't gate the JS.
        role: isPreview ? undefined : 'group', "aria-roledescription": isPreview ? undefined : nlName, "aria-label": isPreview
            ? undefined
            : ((_c = duplicateLabel !== null && duplicateLabel !== void 0 ? duplicateLabel : creationLabel) !== null && _c !== void 0 ? _c : "".concat(t('text.numberLineAriaLabel', {
                start: startNum,
                end: endNum,
            }), ". ").concat(t('text.numberLineHint'))), "aria-disabled": positionLock ? true : undefined, tabIndex: isPreview ? -1 : 0, onFocus: isPreview ? undefined : function (e) {
            if (e.target !== e.currentTarget)
                return;
            if (!selectedTool.includes(id))
                setSelectedTool([id]);
        }, onKeyDown: isPreview ? undefined : onWrapperKeyDown, children: [_jsxs("div", { className: "numberLineTrack", style: {
                    width: svgWidth,
                    height: TOTAL_SVG_HEIGHT,
                }, children: [_jsx("div", { className: "numberLineArrowhead numberLineArrowheadLeft", style: { left: 0, top: '50%' } }), _jsx("div", { className: "numberLineLine", style: { left: ARROWHEAD_SIZE, top: '50%', width: svgWidth - ARROWHEAD_SIZE * 2 } }), _jsx("div", { className: "numberLineArrowhead numberLineArrowheadRight", style: { left: svgWidth - ARROWHEAD_SIZE, top: '50%' } }), ticks.map(function (tick, i) { return (_jsx("div", { className: "numberLineTick", style: { left: lineStartX + tick.px, top: '50%' } }, "tick-".concat(i))); }), (function () {
                        // Estimate visual width of rendered label (fractions are stacked, not inline)
                        var PX_PER_CHAR = 15;
                        var estimateVisualWidth = function (formatted) {
                            var isNeg = formatted.startsWith('-');
                            var work = isNeg ? formatted.slice(1) : formatted;
                            var negWidth = isNeg ? PX_PER_CHAR : 0;
                            // Mixed number: "2\frac{3}{4}" → integer + stacked fraction side by side
                            var mixedMatch = work.match(/^(\d+)\\frac\{(\d+)\}\{(\d+)\}$/);
                            if (mixedMatch) {
                                var intW = mixedMatch[1].length * PX_PER_CHAR;
                                var fracW = Math.max(mixedMatch[2].length, mixedMatch[3].length) * PX_PER_CHAR;
                                return negWidth + intW + fracW;
                            }
                            // Fraction: "\frac{1}{12}" → stacked, width = wider of num/denom
                            var fracMatch = work.match(/^\\frac\{(\d+)\}\{(\d+)\}$/);
                            if (fracMatch) {
                                return negWidth + Math.max(fracMatch[1].length, fracMatch[2].length) * PX_PER_CHAR;
                            }
                            // Integer/decimal — plain text
                            return negWidth + work.length * PX_PER_CHAR;
                        };
                        var estLabelWidth = labels.reduce(function (max, l) { return Math.max(max, estimateVisualWidth(l.formatted)); }, 0);
                        var maxAllowedWidth = 55;
                        var labelGap = labels.length > 1 ? Math.abs(labels[1].px - labels[0].px) : Infinity;
                        var shouldAlternate = estLabelWidth > maxAllowedWidth && labelGap < estLabelWidth;
                        return labels.map(function (label, i) {
                            var isMasked = !labelsVisible && !unmaskedLabels.includes(label.value);
                            var alternateOffset = shouldAlternate && i % 2 === 1 ? 45 : 0;
                            if (isMasked) {
                                return (_jsx("button", { className: "numberLineMask", style: {
                                        left: lineStartX + label.px - 13,
                                        top: LINE_Y + LABEL_OFFSET + 3 + alternateOffset,
                                    }, onClick: function (e) {
                                        e.stopPropagation();
                                        handleMaskClick(label.value);
                                    } }, "label-".concat(i)));
                            }
                            var isMaskable = !labelsVisible;
                            return (_jsx("span", { className: "numberLineLabel ".concat(isMaskable ? 'numberLineLabel--maskable' : ''), style: {
                                    left: lineStartX + label.px,
                                    top: LINE_Y + LABEL_OFFSET + alternateOffset,
                                    // display: !data.scalingToManipulatives ? 'none' : 'inline-flex',
                                }, role: isMaskable ? 'button' : undefined, tabIndex: isMaskable ? 0 : undefined, onClick: isMaskable
                                    ? function (e) {
                                        e.stopPropagation();
                                        handleMaskClick(label.value);
                                    }
                                    : undefined, onKeyDown: isMaskable
                                    ? function (e) {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            handleMaskClick(label.value);
                                        }
                                    }
                                    : undefined, children: _jsx(NumberLineLabel, { value: label.formatted }) }, "label-".concat(i)));
                        });
                    })(), !isPreview && isFocused && (_jsxs(_Fragment, { children: [_jsx("button", { className: "numberLineDragHandle numberLineDragHandleLeft", style: { left: 0, top: '50%', cursor: (lock === null || lock === void 0 ? void 0 : lock.editing) ? 'default' : 'ew-resize' }, onMouseDown: function (e) { return onEndDrag('left', e); }, onTouchStart: function (e) { return onEndDrag('left', e); }, "aria-hidden": "true", tabIndex: -1, disabled: lock === null || lock === void 0 ? void 0 : lock.editing }), _jsx("button", { className: "numberLineDragHandle numberLineDragHandleRight", style: { right: 0, top: '50%', cursor: (lock === null || lock === void 0 ? void 0 : lock.editing) ? 'default' : 'ew-resize' }, onMouseDown: function (e) { return onEndDrag('right', e); }, onTouchStart: function (e) { return onEndDrag('right', e); }, "aria-hidden": "true", tabIndex: -1, disabled: lock === null || lock === void 0 ? void 0 : lock.editing })] }))] }), !isPreview && isFocused && (_jsx("button", { type: "button", className: "numberLineKebabBtn", "aria-label": t('text.openOptions') || 'Open options', "aria-haspopup": "menu", style: {
                    // Sit just past the right arrow tip (left edge of the
                    // arrowhead = svgWidth - ARROWHEAD_SIZE; tip = svgWidth)
                    // plus a small visual gap. Vertically center against the
                    // line, which itself sits at TOTAL_SVG_HEIGHT / 2.
                    left: svgWidth + 8,
                    top: TOTAL_SVG_HEIGHT / 2,
                    transform: 'translateY(-50%)',
                }, onMouseDown: function (e) { return e.stopPropagation(); }, onTouchStart: function (e) { return e.stopPropagation(); }, onClick: function (e) {
                    e.stopPropagation();
                    if (!selectedTool.includes(id))
                        setSelectedTool([id]);
                    var rect = e.currentTarget.getBoundingClientRect();
                    setContextMenu({ x: rect.right, y: rect.top, manipulativeId: id });
                }, children: _jsx("img", { src: "./assets/images/icons/kebabN.svg", alt: "", "aria-hidden": "true" }) })), showScaleSnapNotice && (_jsx(Popup, { isOpen: showScaleSnapNotice, onClose: function () { return setShowScaleSnapNotice(false); }, ariaLabelledBy: "nlScaleSnapNotice-title-".concat(id), ariaDescribedBy: "nlScaleSnapNotice-body-".concat(id), children: _jsxs("div", { className: "nlScaleSnapNoticeContainer", children: [_jsx("h1", { id: "nlScaleSnapNotice-title-".concat(id), className: "nlScaleSnapNoticeTitle", children: t('text.jumpBarSnapDisabledTitle') || 'Snap to Grid Disabled' }), _jsx("hr", { className: "popupHr", "aria-hidden": "true" }), _jsx("div", { id: "nlScaleSnapNotice-body-".concat(id), className: "nlScaleSnapNoticeBody", children: t('text.jumpBarSnapDisabledBody') ||
                                'Snap jumps to underlying grid has been disabled because the number line is no longer at scale 1.' }), _jsx(ButtonComponent, { type: "textType1", text: t('text.ok'), defaultCSS: "nlScaleSnapNoticeBtn", normalCSS: "nlScaleSnapNoticeBtn", clickHandler: function () { return setShowScaleSnapNotice(false); } })] }) }))] }));
};
export default NumberLineManipulative;
